import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';

import cn from 'classnames';
import { Button, LoaderSpinner, TextField } from 'components/UtilElements';
import { AuthAction, AuthActions, AuthorizationResponse } from 'containers/AuthStateManager';
import { API, request } from 'redux/api';
import { RootState } from 'redux/reducers';
// import { AuthScreens } from 'globals/constants';
import { setSessionToken } from 'utils/session';

const getVerificationCode = (context: VerifyCodeContext) => {
  return request({ url: API.LTI_VERIFY_GET_CODE, params: { context } });
};

const verifyCode = (verificationCode: string) => {
  return request({
    method: 'PATCH',
    url: API.LTI_VERIFY_TEMP_CODE,
    data: { verificationCode },
  });
};

const verifyNewEmail = async (verificationCode: string, linkToken: string) => {
  const response = await request<AuthorizationResponse>({
    url: API.LTI_LINK_VERIFY_NEW_EMAIL,
    method: 'POST',
    data: { verificationCode, linkToken },
  });
  setSessionToken(response.authToken);
  return response;
};

interface VerifyCodeProps {
  context: VerifyCodeContext;
  linkToken: string;
  accessData: SessionData | null;
  setAuthState(action: AuthAction): void;
}

const VerifyCode: FC<VerifyCodeProps> = ({ context, linkToken, accessData, setAuthState }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resendCount, setResendCount] = useState(0);

  const resendCode = useCallback(async () => {
    try {
      if (resendCount < 5) {
        setIsResending(true);
        await getVerificationCode(context);
        setResendCount(resendCount + 1);
      } else {
        alert('Please try after some time or contact Acadly support (support@acadly.com)');
      }
    } catch (e: any) {
      console.error(e.response ? e.response.data.message : e.message);
    } finally {
      setIsResending(false);
    }
  }, [context, resendCount]);

  const onSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (code.trim() === '') {
        return setError("Verification code can't be empty");
      } else if (code.trim().length !== 8) {
        return setError('Invalid verification code');
      }

      try {
        setError('');
        setIsLoading(true);

        switch (context) {
          case 'link': {
            const response = await verifyNewEmail(code, linkToken);
            setAuthState({
              type: AuthActions.AUTHORIZE_ACADLY,
              payload: response,
            });
            break;
          }
          case 'email': {
            await verifyCode(code);
            setAuthState({
              type: AuthActions.GOTO_SET_PASSWORD,
              payload: { isResettingPassword: false },
            });
            break;
          }
          case 'password': {
            await verifyCode(code);
            setAuthState({
              type: AuthActions.GOTO_SET_PASSWORD,
              payload: { isResettingPassword: true },
            });
            break;
          }
          default:
            break;
        }
      } catch (err) {
        console.error(err);
        setIsLoading(false);
        setError('Invalid verification code');
      }
    },
    [code, context, linkToken, setAuthState]
  );

  const content = useMemo(() => {
    switch (context) {
      case 'link':
        return {
          title: <>Linking accounts - verify new email address</>,
          message: (
            <>
              To continue linking your account - please enter the verification code sent to{' '}
              <strong>{accessData?.emailId}</strong>
            </>
          ),
          note: (
            <>
              Once your accounts are linked, you can export your LMS courses into your existing Acadly account
            </>
          ),
        };
      case 'email':
        return {
          title: <>Creating a new account - verify new email address</>,
          message: (
            <>
              To continue creating your account - please enter the verification code sent to{' '}
              <strong>{accessData?.emailId}</strong>
            </>
          ),
          note: null,
          // note: (
          //   <>
          //     Already an Acadly user?{' '}
          //     <button
          //       className='auth__link'
          //       onClick={() => {
          //         setAuthState({
          //           type: AuthActions.SET_SCREEN_WITH_CONTEXT,
          //           payload: { context: 'link', screen: AuthScreens.GET_EMAIL },
          //         });
          //       }}
          //     >
          //       Link it to this email address
          //     </button>
          //   </>
          // ),
        };
      default:
        return {
          title: <>Resetting password</>,
          message: (
            <>
              Please enter the password reset code sent to <strong>{accessData?.emailId}</strong>
            </>
          ),
          note: null,
        };
    }
  }, [accessData, context]);

  if (!accessData) return null;

  return (
    <>
      <h2 className='auth__title'>{content.title}</h2>
      <form className='auth__panel' onSubmit={onSubmit}>
        <p className='auth__message'>{content.message}</p>
        <TextField
          hasError={!!error}
          selector='auth__input'
          wrapperClassName='auth__input-wrapper'
          placeholder='Type verification code here'
          onchange={(e) => setCode(e.target.value)}
          value={code}
          type='text'
        />
        <div className={cn('auth__error', { shake: !!error })}>{error}</div>
        <div className={cn('auth__action-container', 'auth__action-container--horizontal')}>
          {isResending ? (
            <div className={cn('auth__action', 'auth__action--md')}>
              <LoaderSpinner />
            </div>
          ) : (
            <Button
              variant='link'
              color='info'
              size='small'
              className={cn('auth__action', 'auth__action--md')}
              onClick={resendCode}
            >
              {resendCount === 0 ? 'Resend code' : `Resend code (${resendCount}/5)`}
            </Button>
          )}
          <Button
            variant='outlined'
            color='info'
            size='large'
            type='submit'
            isLoading={isLoading}
            className={cn('auth__action', 'auth__action--sm')}
            preventMinWidth
          >
            Verify
          </Button>
        </div>
        <p className='auth__note'>{content.note}</p>
      </form>
    </>
  );
};

const mapStateToProps = ({ getAccess }: RootState) => {
  return { accessData: getAccess };
};

export default connect(mapStateToProps)(VerifyCode);
